import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';

import { adaptFacetToUIFacet } from 'src/adapters/storeToUI/adaptFacetToUIFacet';
import { adaptSortOptionsToUISortOptions } from 'src/adapters/storeToUI/adaptSortOptionsToUISortOptions';
import { ListingsControls } from 'src/components/organisms/ListingsControls/ListingsControls';
import type {
  ChangedFacetDetails,
  ChangedSortDetails,
} from 'src/components/organisms/ListingsControlsOptions/types';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { updatePageURLWithQuery } from 'src/redux/products/thunks/updatePageURLWithQuery';
import { TriggerName } from 'src/redux/products/types';
import { actions } from 'src/redux/productsQuery/slice';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';

export const ListingsControlsContainer = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: ReduxApplicationState) => state);
  const facets = state.productsQuery.facets;
  const sortOptions = state.productsQuery.sortOptions;

  const onAllFacetsVisible = () => {
    sendGAEvent({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'filter impression',
      event_label: 'all filters viewed',
    });
  };

  const onSortOptionChange = ({ activeTitle, activeValue }: ChangedSortDetails) => {
    batch(() => {
      dispatch(actions.changeSortOption(activeValue));
      dispatch(requestProducts({ triggerName: TriggerName.CHANGE_SORT }));
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });

    sendGAEvent({
      event: 'custom_event',
      event_category: 'Sort',
      event_action: `select sort ${activeValue}`,
      event_label: activeTitle,
    });
  };

  const onFacetOptionChange = ({ ...changedFacet }: ChangedFacetDetails) => {
    batch(() => {
      dispatch(actions.changeFacetOption(changedFacet));
      dispatch(requestProducts({ triggerName: TriggerName.CHANGE_FACETS }));
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });

    const changedOption = changedFacet.options[0];

    sendGAEvent({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: `${changedOption.active ? 'select' : 'de-select'} filter ${changedFacet.title}`,
      event_label: changedOption.title!,
    });
  };

  return (
    <ListingsControls
      facets={facets.map(adaptFacetToUIFacet)}
      onAllFacetsVisible={onAllFacetsVisible}
      onFacetOptionChange={onFacetOptionChange}
      onSortOptionChange={onSortOptionChange}
      sortOptions={adaptSortOptionsToUISortOptions(sortOptions)}
    />
  );
};
